import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import IMG from "@/constants/images";
import { ArrowUpRight } from "lucide-react";

export default function AboutPage() {
  const { t } = useI18n();

  return (
    <div data-testid="page-about">
      {/* INTRO */}
      <section className="pt-20 md:pt-28 pb-16">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="eyebrow">— {t.about.eyebrow}</div>
            <h1 className="mt-6 text-[clamp(2.5rem,6vw,5.5rem)] leading-[1.02] tracking-[-0.02em]">
              {t.about.title}
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
              {t.about.lead}
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={IMG.craftTexture} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* HISTORY */}
      <section className="container-x py-20 md:py-28 border-t border-[hsl(var(--line))]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow">— 01</div>
            <h2 className="mt-4 text-3xl md:text-4xl leading-[1.05]">
              {t.about.historyTitle}
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
              {t.about.historyBody}
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[hsl(var(--bg-elev))] py-24 md:py-32">
        <div className="container-x">
          <div className="eyebrow">— 02</div>
          <h2 className="mt-4 text-3xl md:text-5xl leading-[1.05] max-w-3xl">
            {t.about.valuesTitle}
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[hsl(var(--line))] border border-[hsl(var(--line))]">
            {t.about.values.map((v, i) => (
              <div key={i} className="bg-[hsl(var(--bg))] p-10 min-h-[260px] flex flex-col">
                <div className="stat-num text-[hsl(var(--ocre-deep))] text-2xl">0{i + 1}</div>
                <h3 className="mt-8 text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {v.t}
                </h3>
                <p className="mt-4 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">{v.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="container-x py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bg-[hsl(var(--stone))]">
              <img src={IMG.founderPortrait} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="eyebrow">— {t.about.founderEyebrow}</div>
            <h2 className="mt-4 text-3xl md:text-5xl leading-[1.05]">
              {t.about.founderTitle}
            </h2>
            <p className="mt-8 text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
              {t.about.founderBody}
            </p>
            <div className="mt-10 border-t border-[hsl(var(--line))] pt-6 grid grid-cols-2 gap-6 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Founded</div>
                <div className="mt-1 stat-num text-2xl">2021</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Team</div>
                <div className="mt-1 stat-num text-2xl">4 craftsmen</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="bg-[hsl(var(--umber))] text-[hsl(var(--bg))] py-24 md:py-32">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="aspect-[5/4] overflow-hidden">
              <img src={IMG.team} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <div className="eyebrow text-[hsl(var(--ocre))]">— 03</div>
            <h2 className="mt-4 text-3xl md:text-5xl leading-[1.05]">
              {t.about.teamTitle}
            </h2>
            <p className="mt-6 text-[hsl(var(--bg))]/80 text-lg leading-relaxed">
              {t.about.teamBody}
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-2 mt-10 px-7 py-3.5 text-sm uppercase tracking-[0.18em] font-medium border border-[hsl(var(--bg))] text-[hsl(var(--bg))] hover:bg-[hsl(var(--bg))] hover:text-[hsl(var(--ink))] transition-all duration-300"
            >
              {t.nav.quote} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
